/** @typedef {keyof typeof EVENT_TYPE_LABELS} EventTypeKey */

export const EVENT_TYPE_LABELS = {
  dinner: 'Dinner',
  coffee: 'Coffee Meetup',
  networking: 'Networking',
  wellness: 'Wellness',
  workshop: 'Workshop',
  partner: 'Partner Event',
  community: 'Community Event',
};

/** @type {EventTypeKey[]} */
export const EVENT_TYPE_KEYS = Object.keys(EVENT_TYPE_LABELS);

const LEGACY_LABEL_TO_KEY = {
  Dinner: 'dinner',
  'Coffee Meetup': 'coffee',
  Networking: 'networking',
  Wellness: 'wellness',
  Workshop: 'workshop',
  'Partner Event': 'partner',
  'Community Event': 'community',
};

const LEGACY_SLUG_TO_KEY = {
  'coffee-meetup': 'coffee',
  coffee_meetup: 'coffee',
  'partner-event': 'partner',
  partner_event: 'partner',
  'community-event': 'community',
  community_event: 'community',
};

/**
 * Maps legacy display strings / slugs to canonical API keys.
 * @param {string | null | undefined} raw
 * @returns {string} canonical key, or '' if empty
 */
export const normalizeEventTypeKey = (raw) => {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';

  if (EVENT_TYPE_LABELS[trimmed]) return trimmed;

  const lower = trimmed.toLowerCase();
  if (EVENT_TYPE_LABELS[lower]) return lower;

  const fromLabel = LEGACY_LABEL_TO_KEY[trimmed] ?? LEGACY_LABEL_TO_KEY[lower];
  if (fromLabel) return fromLabel;

  const fromSlug = LEGACY_SLUG_TO_KEY[lower] ?? LEGACY_SLUG_TO_KEY[trimmed];
  if (fromSlug) return fromSlug;

  const fromLabelInsensitive = EVENT_TYPE_KEYS.find(
    (key) => EVENT_TYPE_LABELS[key].toLowerCase() === lower,
  );
  if (fromLabelInsensitive) return fromLabelInsensitive;

  return lower;
};

/**
 * @param {string | null | undefined} key
 * @returns {string}
 */
export const getEventTypeLabel = (key) => {
  if (!key) return '';
  const normalized = normalizeEventTypeKey(key);
  return EVENT_TYPE_LABELS[normalized] ?? key;
};

/** @param {string | null | undefined} type */
export const isValidEventTypeKey = (type) => EVENT_TYPE_KEYS.includes(normalizeEventTypeKey(type));

export const EVENT_TYPE_FILTERS = [
  { value: '', label: 'All types' },
  ...EVENT_TYPE_KEYS.map((key) => ({ value: key, label: EVENT_TYPE_LABELS[key] })),
];

export const EVENT_TYPE_OPTIONS = EVENT_TYPE_KEYS.map((key) => ({
  value: key,
  label: EVENT_TYPE_LABELS[key],
}));

export const DEFAULT_EVENT_TYPE_KEY = 'dinner';

export const EVENT_TYPE_BADGE_STYLES = {
  dinner: { backgroundColor: '#E6A89E', color: '#FFFFFF' },
  coffee: { backgroundColor: '#A1887F', color: '#FFFFFF' },
  networking: { backgroundColor: '#B39DDB', color: '#FFFFFF' },
  wellness: { backgroundColor: '#81C784', color: '#FFFFFF' },
  workshop: { backgroundColor: '#90CAF9', color: '#FFFFFF' },
  partner: { backgroundColor: '#FFB74D', color: '#FFFFFF' },
  community: { backgroundColor: '#4DB6AC', color: '#FFFFFF' },
};

export const getTypeBadgeStyle = (typeKey) => {
  const key = normalizeEventTypeKey(typeKey);
  return (
    EVENT_TYPE_BADGE_STYLES[key] ?? {
      backgroundColor: '#BDBDBD',
      color: '#FFFFFF',
    }
  );
};
